import { useCallback, useEffect, useRef, useState } from 'react'

import { api, ApiError, type ConnectedApp } from '../api'
import { formatRelative } from '../relativeTime'
import { ProblemBanner } from './Problem'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/** The "Connected apps" section of the Account page: MCP clients the user has authorised. */
export function ConnectedApps() {
  const [apps, setApps] = useState<ConnectedApp[] | null>(null)
  const [loadError, setLoadError] = useState<unknown>(null)
  const [confirming, setConfirming] = useState<string | null>(null)
  const [revoking, setRevoking] = useState<string | null>(null)
  const [revokeErrors, setRevokeErrors] = useState<Record<string, unknown>>({})
  const heading = useRef<HTMLHeadingElement>(null)
  const cancelButton = useRef<HTMLButtonElement>(null)
  const revokeButtons = useRef(new Map<string, HTMLButtonElement>())
  const restoreFocusTo = useRef<string | null>(null)
  const mounted = useRef(true)

  const load = useCallback(() => {
    setLoadError(null)
    api
      .listConnectedApps()
      .then(({ items }) => {
        if (mounted.current) setApps(items)
      })
      .catch((failure: unknown) => {
        if (mounted.current) setLoadError(failure)
      })
  }, [])

  useEffect(() => {
    mounted.current = true
    load()
    return () => {
      mounted.current = false
    }
  }, [load])

  // Move focus into the confirmation so keyboard users land on it; Cancel is the safe default.
  useEffect(() => {
    if (confirming) {
      cancelButton.current?.focus()
    } else if (restoreFocusTo.current) {
      // The Revoke button is re-created when the confirmation closes; refocus the new one.
      revokeButtons.current.get(restoreFocusTo.current)?.focus()
      restoreFocusTo.current = null
    }
  }, [confirming])

  const cancel = (clientId: string) => {
    restoreFocusTo.current = clientId
    setConfirming(null)
  }

  const revoke = (app: ConnectedApp) => {
    setRevoking(app.clientId)
    setRevokeErrors((current) => {
      const next = { ...current }
      delete next[app.clientId]
      return next
    })
    api
      .revokeConnectedApp(app.clientId)
      // 404 means the caller has no grant for this client (revoked in another tab, or
      // expired): the goal is met, so refresh rather than report an error.
      .catch((failure: unknown) => {
        if (failure instanceof ApiError && failure.problem.status === 404) return
        throw failure
      })
      .then(() => {
        if (!mounted.current) return
        setRevoking(null)
        setConfirming(null)
        // The row's controls are about to disappear, so park focus on the section heading.
        heading.current?.focus()
        load()
      })
      .catch((failure: unknown) => {
        if (!mounted.current) return
        setRevoking(null)
        restoreFocusTo.current = app.clientId
        setConfirming(null)
        setRevokeErrors((current) => ({ ...current, [app.clientId]: failure }))
      })
  }

  let body
  if (loadError && !apps) {
    body = (
      <>
        <ProblemBanner error={loadError} />
        <div className="row">
          <button onClick={load}>Retry</button>
        </div>
      </>
    )
  } else if (!apps) {
    body = <p className="muted">Loading…</p>
  } else if (apps.length === 0) {
    body = <p className="muted">No apps are connected.</p>
  } else {
    body = (
      <ul className="connected-apps">
        {apps.map((app) => {
          const busy = revoking === app.clientId
          const failure = revokeErrors[app.clientId]
          return (
            <li key={app.clientId} className="connected-app">
              <div className="connected-app-main">
                <strong>{app.name}</strong>{' '}
                {app.verified ? (
                  <span className="muted">Verified: {app.domain}</span>
                ) : (
                  <>
                    <span className="badge">Unverified</span>
                    {app.domain ? <span className="muted"> {app.domain}</span> : null}
                  </>
                )}
                <p className="muted">
                  Authorised {formatDate(app.authorisedAt)}
                  {app.lastUsedAt ? ` · Last active ${formatRelative(app.lastUsedAt)}` : ''}
                </p>
              </div>
              {confirming === app.clientId ? (
                <div
                  role="group"
                  aria-label={`Revoke ${app.name}`}
                  className="connected-app-confirm"
                >
                  <p>Revoke access for {app.name}? It will stop working immediately.</p>
                  <div className="row">
                    <button className="danger confirm" disabled={busy} onClick={() => revoke(app)}>
                      {busy ? 'Revoking…' : 'Confirm'}
                    </button>
                    <button ref={cancelButton} disabled={busy} onClick={() => cancel(app.clientId)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="danger"
                  aria-label={`Revoke ${app.name}`}
                  ref={(element) => {
                    if (element) revokeButtons.current.set(app.clientId, element)
                    else revokeButtons.current.delete(app.clientId)
                  }}
                  onClick={() => setConfirming(app.clientId)}
                >
                  Revoke
                </button>
              )}
              {failure ? (
                <div className="connected-app-error">
                  <ProblemBanner error={failure} />
                </div>
              ) : null}
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <section aria-labelledby="connected-apps-heading">
      <h2 id="connected-apps-heading" ref={heading} tabIndex={-1}>
        Connected apps
      </h2>
      <p className="muted">
        Apps you have allowed to use Flightdeck on your behalf, such as AI assistants.
      </p>
      {body}
    </section>
  )
}
